import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { REFRESH_EVENT } from '../lib/notificationActions';
import { getDocLabel } from '../lib/docLabels';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
    Users, Search, MapPin, Briefcase, Send, Eye, FileText, Video, Shield,
    Check, X, Clock, Upload, Plus, Calendar, ExternalLink, Image as ImageIcon, Paperclip, HeartPulse,
    ChevronDown, ChevronRight
} from 'lucide-react';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import EmailPreferencesPanel from '../components/EmailPreferencesPanel';
import VideoIntroPlayer from '../components/VideoIntroPlayer';
import EmployerJobBoard from '../components/EmployerJobBoard';

const INTEREST_FILTERS = ['Security', 'Cleaner', 'Driver', 'Construction', 'Carpenter', 'Electrician', 'Plumber', 'Cook'];
const WORKER_COUNTRIES = ['Uganda', 'Kenya', 'Ethiopia', 'Tanzania', 'Ghana', 'Nigeria', 'Rwanda', 'Egypt', 'Morocco', 'Sudan'];

// Gulf country <-> currency mapping (Fix 1)
const COUNTRY_TO_CURRENCY = {
    'UAE': 'AED',
    'United Arab Emirates': 'AED',
    'Saudi Arabia': 'SAR',
    'Qatar': 'QAR',
    'Kuwait': 'KWD',
    'Bahrain': 'BHD',
    'Oman': 'OMR',
};
const CURRENCY_TO_COUNTRY = {
    'AED': 'UAE',
    'SAR': 'Saudi Arabia',
    'QAR': 'Qatar',
    'KWD': 'Kuwait',
    'BHD': 'Bahrain',
    'OMR': 'Oman',
    // USD intentionally omitted — used in multiple countries
};

export default function EmployerDashboard() {
    const { user, refreshUser } = useAuth();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState((searchParams.get('tab') === 'jobs') ? 'posted-jobs' : (searchParams.get('tab') || 'talent'));
    const [workers, setWorkers] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedWorker, setSelectedWorker] = useState(null);
    const [workerDocs, setWorkerDocs] = useState([]);
    const [workerPhotoBlobUrl, setWorkerPhotoBlobUrl] = useState(null);
    const [showProfile, setShowProfile] = useState(false);
    const [showOfferForm, setShowOfferForm] = useState(false);
    const [showJobForm, setShowJobForm] = useState(false);
    const [showInterviewForm, setShowInterviewForm] = useState(false);
    // Inline success banner shown inside the open worker profile after a
    // successful Schedule Interview / Send Offer submission (the profile stays
    // open per the layered-modal spec). Use a ref'd timer so reopening another
    // profile within the 4s window doesn't flash a stale banner.
    const [profileSuccessMsg, setProfileSuccessMsg] = useState('');
    const profileSuccessTimerRef = useRef(null);
    const clearProfileSuccessTimer = () => {
        if (profileSuccessTimerRef.current) {
            clearTimeout(profileSuccessTimerRef.current);
            profileSuccessTimerRef.current = null;
        }
    };
    const showProfileSuccess = (msg) => {
        clearProfileSuccessTimer();
        setProfileSuccessMsg(msg);
        profileSuccessTimerRef.current = setTimeout(() => {
            setProfileSuccessMsg('');
            profileSuccessTimerRef.current = null;
        }, 4000);
    };
    const [interestFilter, setInterestFilter] = useState([]); // string[] — multi-select; [] = all
    const [searchQuery, setSearchQuery] = useState('');
    const [countryFilter, setCountryFilter] = useState([]); // string[] — multi-select; [] = all
    const [dateSortOrder, setDateSortOrder] = useState('newest'); // 'newest' | 'oldest'

    // Candidate tracking search/sort (Fix 4C)
    const [candidateSearch, setCandidateSearch] = useState('');
    const [candidateSort, setCandidateSort] = useState('recent'); // 'accepted' | 'rejected' | 'pending' | 'recent' | 'oldest'

    // Travel tracker sort (Fix 4D)
    const [travelSort, setTravelSort] = useState('soonest'); // 'soonest' | 'latest' | 'name' | 'visa-status'

    // Document preview modal (Fix 5)
    const [previewDoc, setPreviewDoc] = useState(null);

    // Accepted-candidate documents dialog (Fix 6)
    const [showAcceptedDocs, setShowAcceptedDocs] = useState(false);
    const [acceptedDocsLoading, setAcceptedDocsLoading] = useState(false);
    const [acceptedWorker, setAcceptedWorker] = useState(null);
    const [acceptedDocsList, setAcceptedDocsList] = useState([]);
    const [acceptedPhotoBlobUrl, setAcceptedPhotoBlobUrl] = useState(null);

    const openAcceptedDocs = async (workerId) => {
        setShowAcceptedDocs(true);
        setAcceptedDocsLoading(true);
        setAcceptedWorker(null);
        setAcceptedDocsList([]);
        setAcceptedPhotoBlobUrl(null);
        try {
            const { data } = await api.get(`/employer/candidates/${workerId}/documents`);
            setAcceptedWorker(data.worker);
            setAcceptedDocsList(data.documents || []);
            // Lazy-load photo blob
            const photoDoc = data.documents.find(d => d.doc_type === 'photo');
            if (photoDoc?.storage_path) {
                try {
                    const resp = await api.get(`/files/${photoDoc.storage_path}`, { responseType: 'blob' });
                    setAcceptedPhotoBlobUrl(URL.createObjectURL(resp.data));
                } catch (err) {
                    console.error('Failed to load worker photo:', err);
                }
            }
        } catch (err) {
            console.error('Failed to load accepted-candidate docs:', err);
        } finally {
            setAcceptedDocsLoading(false);
        }
    };

    // Job form
    const [jobTitle, setJobTitle] = useState('');
    const [jobSalary, setJobSalary] = useState('');
    const [jobCurrency, setJobCurrency] = useState('USD');
    const [jobBenefits, setJobBenefits] = useState('');
    const [jobLocation, setJobLocation] = useState('');
    const [jobCountry, setJobCountry] = useState('UAE');
    const [jobDescription, setJobDescription] = useState('');
    const [jobVisibility, setJobVisibility] = useState('public'); // 'public' | 'private'
    const [savingJob, setSavingJob] = useState(false);

    // Offer - select from jobs
    const [selectedJobId, setSelectedJobId] = useState('');
    const [sending, setSending] = useState(false);

    // Interview form (Schedule)
    const [interviewDate, setInterviewDate] = useState('');
    const [interviewTime, setInterviewTime] = useState('');
    const [interviewMeet, setInterviewMeet] = useState('');
    const [schedulingInterview, setSchedulingInterview] = useState(false);

    // Interview management (Reschedule + Cancel + Interviews tab)
    const [interviews, setInterviews] = useState([]);
    const [interviewPhotos, setInterviewPhotos] = useState({}); // workerId -> blob url
    const [showPastInterviews, setShowPastInterviews] = useState(false);
    const [rescheduleTarget, setRescheduleTarget] = useState(null); // interview object
    const [rescheduleDate, setRescheduleDate] = useState('');
    const [rescheduleTime, setRescheduleTime] = useState('');
    const [rescheduleMeet, setRescheduleMeet] = useState('');
    const [savingReschedule, setSavingReschedule] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null); // interview object
    const [cancellingInterview, setCancellingInterview] = useState(false);
    const [interviewsTabSuccess, setInterviewsTabSuccess] = useState('');
    const interviewsTabSuccessTimerRef = useRef(null);
    const showInterviewsTabSuccess = (msg) => {
        if (interviewsTabSuccessTimerRef.current) {
            clearTimeout(interviewsTabSuccessTimerRef.current);
        }
        setInterviewsTabSuccess(msg);
        interviewsTabSuccessTimerRef.current = setTimeout(() => {
            setInterviewsTabSuccess('');
            interviewsTabSuccessTimerRef.current = null;
        }, 4000);
    };

    // Visa management
    const [showVisaDialog, setShowVisaDialog] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);

    // Worker card photo blobs
    const [cardPhotos, setCardPhotos] = useState({});

    const fetchData = useCallback(async () => {
        try {
            // Skip talent pool fetch if account is not approved (returns 403)
            const isApproved = (user?.approval_status || 'approved') === 'approved';
            const [poolRes, candRes, jobsRes, interviewsRes] = await Promise.all([
                isApproved ? api.get('/employer/talent-pool') : Promise.resolve({ data: { workers: [] } }),
                api.get('/employer/candidates'),
                api.get('/employer/jobs'),
                api.get('/employer/interviews'),
            ]);
            setWorkers(poolRes.data.workers);
            setCandidates(candRes.data.candidates);
            setJobs(jobsRes.data.jobs);
            setInterviews(interviewsRes.data.interviews || []);
            // Load photos for workers with photo_doc
            const photos = {};
            for (const w of poolRes.data.workers) {
                if (w.photo_doc?.storage_path) {
                    try {
                        const resp = await api.get(`/files/${w.photo_doc.storage_path}`, { responseType: 'blob' });
                        photos[w.id] = URL.createObjectURL(resp.data);
                    } catch { /* no photo */ }
                }
            }
            setCardPhotos(photos);
            // Load interview worker thumbnails (only ones not already in talent-pool photos)
            const ivPhotos = {};
            for (const iv of (interviewsRes.data.interviews || [])) {
                if (iv.worker_photo_path && !photos[iv.worker_id] && !ivPhotos[iv.worker_id]) {
                    try {
                        const resp = await api.get(`/files/${iv.worker_photo_path}`, { responseType: 'blob' });
                        ivPhotos[iv.worker_id] = URL.createObjectURL(resp.data);
                    } catch { /* no photo */ }
                }
            }
            setInterviewPhotos(ivPhotos);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [user?.approval_status]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // React to ?tab= changes (e.g. from notification click navigation)
    useEffect(() => {
        const t = searchParams.get('tab');
        if (t && ['talent', 'jobs', 'posted-jobs', 'candidates', 'travel', 'interviews'].includes(t)) {
            // Legacy `jobs` value redirects to the new posted-jobs tab.
            setActiveTab(t === 'jobs' ? 'posted-jobs' : t);
        }
    }, [searchParams]);

    // Silent refresh when a notification arrives or is clicked
    useEffect(() => {
        const handler = (e) => {
            const keys = e.detail?.keys || [];
            if (keys.includes('candidates') || keys.includes('offers') || keys.includes('interviews')) {
                fetchData();
            }
            if (keys.includes('me')) {
                refreshUser();
                fetchData();
            }
        };
        window.addEventListener(REFRESH_EVENT, handler);
        return () => window.removeEventListener(REFRESH_EVENT, handler);
    }, [fetchData, refreshUser]);

    // Poll for approval status updates while pending (every 30s)
    useEffect(() => {
        const status = user?.approval_status;
        if (status !== 'pending') return;
        const interval = setInterval(() => { refreshUser(); }, 30000);
        return () => clearInterval(interval);
    }, [user?.approval_status, refreshUser]);

    const viewWorkerProfile = async (workerId) => {
        try {
            const { data } = await api.get(`/employer/workers/${workerId}`);
            setSelectedWorker(data.worker);
            setWorkerDocs(data.documents);
            // Load photo
            const photoDocs = data.documents.filter(d => d.doc_type === 'photo');
            if (photoDocs.length > 0) {
                try {
                    const resp = await api.get(`/files/${photoDocs[0].storage_path}`, { responseType: 'blob' });
                    setWorkerPhotoBlobUrl(URL.createObjectURL(resp.data));
                } catch { setWorkerPhotoBlobUrl(null); }
            } else { setWorkerPhotoBlobUrl(null); }
            setShowProfile(true);
        } catch (err) { console.error(err); }
    };

    // Bridge helper for the Job Board applicants panel: opens the worker
    // profile modal and then triggers the requested action (Send Offer or
    // Schedule Interview) once the worker data is loaded. Reuses the
    // existing offer / interview dialogs that live in this file.
    const openWorkerAndAction = async (workerId, action) => {
        try {
            const { data } = await api.get(`/employer/workers/${workerId}`);
            setSelectedWorker(data.worker);
            setWorkerDocs(data.documents || []);
            const photoDocs = (data.documents || []).filter(d => d.doc_type === 'photo');
            if (photoDocs.length > 0) {
                try {
                    const resp = await api.get(`/files/${photoDocs[0].storage_path}`, { responseType: 'blob' });
                    setWorkerPhotoBlobUrl(URL.createObjectURL(resp.data));
                } catch { setWorkerPhotoBlobUrl(null); }
            } else { setWorkerPhotoBlobUrl(null); }
            setShowProfile(true);
            if (action === 'offer') setShowOfferForm(true);
            else if (action === 'interview') setShowInterviewForm(true);
        } catch (err) { console.error(err); }
    };

    const createJob = async () => {
        setSavingJob(true);
        try {
            await api.post('/employer/jobs', {
                title: jobTitle, salary: jobSalary, currency: jobCurrency, benefits: jobBenefits,
                location: jobLocation, country: jobCountry, description: jobDescription,
                visibility: jobVisibility,
            });
            setShowJobForm(false);
            resetJobForm();
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setSavingJob(false); }
    };

    const resetJobForm = () => {
        setJobTitle(''); setJobSalary(''); setJobCurrency('USD'); setJobBenefits('');
        setJobLocation(''); setJobCountry('UAE'); setJobDescription(''); setJobVisibility('public');
    };

    const deleteJob = async (jobId) => {
        try { await api.delete(`/employer/jobs/${jobId}`); await fetchData(); }
        catch (err) { console.error(err); }
    };

    const sendOffer = async () => {
        if (!selectedWorker || !selectedJobId) return;
        setSending(true);
        try {
            await api.post('/employer/offer', { worker_id: selectedWorker.id, job_id: selectedJobId });
            // Close ONLY the offer form (Layer 3); worker profile (Layer 2) stays open.
            setShowOfferForm(false);
            setSelectedJobId('');
            setProfileSuccessMsg('Offer sent successfully');
            setTimeout(() => setProfileSuccessMsg(''), 4000);
            await fetchData();
            // Signal any mounted Job Board applicants panel to refetch so a
            // matching pending application flips to 'Offer Sent' visually.
            window.dispatchEvent(new CustomEvent('gulfbridge:applications-changed'));
        } catch (err) { console.error(err); }
        finally { setSending(false); }
    };

    const scheduleInterview = async () => {
        if (!selectedWorker) return;
        setSchedulingInterview(true);
        try {
            await api.post('/employer/interviews', {
                worker_id: selectedWorker.id,
                date: interviewDate, time: interviewTime, meet_link: interviewMeet
            });
            // Close ONLY the interview form (Layer 3); worker profile (Layer 2) stays open.
            setShowInterviewForm(false);
            setInterviewDate(''); setInterviewTime(''); setInterviewMeet('');
            showProfileSuccess('Interview scheduled successfully');
            // Refresh interviews so the Interviews tab + profile banner reflect it.
            fetchData();
        } catch (err) { console.error(err); }
        finally { setSchedulingInterview(false); }
    };

    // Resolve an interview's display status — must mirror the worker dashboard
    // logic so both sides show the same label for the same record.
    // cancelled > upcoming > live (15-min grace) > completed (join_clicked) > missed
    const resolveInterviewStatus = (i) => {
        if (!i) return 'upcoming';
        if (i.status === 'cancelled') return 'cancelled';
        if (!i.date || !i.time) return 'upcoming';
        const startMs = new Date(`${i.date}T${i.time}`).getTime();
        if (Number.isNaN(startMs)) return 'upcoming';
        const now = Date.now();
        const GRACE_MS = 15 * 60 * 1000;
        if (now < startMs) return 'upcoming';
        if (now <= startMs + GRACE_MS) return 'live';
        return i.join_clicked ? 'completed' : 'missed';
    };

    const openReschedule = (interview) => {
        setRescheduleTarget(interview);
        setRescheduleDate(interview.date || '');
        setRescheduleTime(interview.time || '');
        setRescheduleMeet(interview.meet_link || interview.zoom_link || '');
    };

    const submitReschedule = async () => {
        if (!rescheduleTarget) return;
        setSavingReschedule(true);
        try {
            await api.put(`/employer/interviews/${rescheduleTarget.id}`, {
                date: rescheduleDate, time: rescheduleTime, meet_link: rescheduleMeet
            });
            setRescheduleTarget(null);
            setRescheduleDate(''); setRescheduleTime(''); setRescheduleMeet('');
            await fetchData();
            // Inline success — show inside profile if profile is open, otherwise on the tab.
            if (showProfile) showProfileSuccess('Interview rescheduled successfully');
            else showInterviewsTabSuccess('Interview rescheduled successfully.');
        } catch (err) { console.error(err); }
        finally { setSavingReschedule(false); }
    };

    const submitCancelInterview = async () => {
        if (!cancelTarget) return;
        setCancellingInterview(true);
        try {
            await api.delete(`/employer/interviews/${cancelTarget.id}`);
            setCancelTarget(null);
            await fetchData();
            if (showProfile) showProfileSuccess('Interview cancelled. The worker has been notified.');
            else showInterviewsTabSuccess('Interview cancelled. The worker has been notified.');
        } catch (err) { console.error(err); }
        finally { setCancellingInterview(false); }
    };

    const updateVisaStatus = async (offerId, status) => {
        try { await api.put(`/employer/offers/${offerId}/visa-status`, { status }); await fetchData(); }
        catch (err) { console.error(err); }
    };

    const calcAge = (dob) => {
        if (!dob) return null;
        const birth = new Date(dob);
        const now = new Date();
        let age = now.getFullYear() - birth.getFullYear();
        if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age--;
        return age > 0 && age < 120 ? age : null;
    };

    // Last active relative time (Fix 6) — only uses last_login; does not expose exact times
    const relativeLastActive = (lastLogin, createdAt) => {
        if (!lastLogin) return 'Recently joined';
        const last = new Date(lastLogin).getTime();
        if (Number.isNaN(last)) return 'Recently joined';
        const diffMs = Date.now() - last;
        const diffMin = Math.max(0, Math.floor(diffMs / 60000));
        const diffHr = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHr / 24);
        if (diffMin < 60) return diffMin <= 5 ? 'Active now' : `Active ${diffMin} minutes ago`;
        if (diffHr < 24) return `Active ${diffHr} ${diffHr === 1 ? 'hour' : 'hours'} ago`;
        if (diffDay === 0) return 'Active today';
        if (diffDay === 1) return 'Active yesterday';
        if (diffDay < 30) return `Active ${diffDay} days ago`;
        const diffMo = Math.floor(diffDay / 30);
        return diffMo < 12 ? `Active ${diffMo} ${diffMo === 1 ? 'month' : 'months'} ago` : 'Active over a year ago';
    };

    // Edit job
    const [editingJob, setEditingJob] = useState(null);
    const [showEditJob, setShowEditJob] = useState(false);
    const [editJobData, setEditJobData] = useState({});

    const startEditJob = (job) => {
        setEditingJob(job);
        setEditJobData({ title: job.title, salary: job.salary, currency: job.currency || 'USD', benefits: job.benefits, location: job.location, country: job.country, description: job.description, visibility: job.visibility || 'public' });
        setShowEditJob(true);
    };

    const saveEditJob = async () => {
        if (!editingJob) return;
        setSavingJob(true);
        try {
            await api.put(`/employer/jobs/${editingJob.id}`, editJobData);
            setShowEditJob(false);
            setEditingJob(null);
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setSavingJob(false); }
    };

    const uploadOfferDoc = async (offerId, file, docType) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('doc_type', docType);
        try { await api.post(`/employer/offers/${offerId}/upload-document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }); await fetchData(); }
        catch (err) { console.error(err); }
    };

    const filteredWorkers = (() => {
        let list = workers.filter(w => {
            if (interestFilter.length > 0) {
                const wInterests = w.interests || w.skills || [];
                if (!interestFilter.some(i => wInterests.includes(i))) return false;
            }
            if (countryFilter.length > 0 && !countryFilter.includes(w.country)) return false;
            if (searchQuery && !w.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
            return true;
        });
        // Sort by date joined
        list = [...list].sort((a, b) => {
            const da = a.created_at || '';
            const db_ = b.created_at || '';
            if (dateSortOrder === 'newest') return db_.localeCompare(da);
            return da.localeCompare(db_);
        });
        return list;
    })();

    const clearFilters = () => {
        setSearchQuery('');
        setInterestFilter([]);
        setCountryFilter([]);
        setDateSortOrder('newest');
    };

    const hasActiveFilters = interestFilter.length > 0 || countryFilter.length > 0 || searchQuery !== '' || dateSortOrder !== 'newest';

    const activeJobs = jobs.filter(j => j.status === 'active');

    const approvalStatus = user?.approval_status || 'approved';
    const isApproved = approvalStatus === 'approved';
    const isRejected = approvalStatus === 'rejected';
    const isPending = approvalStatus === 'pending';

    if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin" /></div>;

    return (
        <div data-testid="employer-dashboard">
            {!isApproved && (
                <div
                    className={`border-2 mb-6 p-4 ${isRejected ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}
                    data-testid="approval-banner"
                >
                    <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 mt-2 rounded-full ${isRejected ? 'bg-red-500' : 'bg-amber-500'} animate-pulse`} />
                        <div className="flex-1">
                            <p className="font-heading font-bold text-sm" data-testid="approval-banner-title">
                                Account status: {isRejected ? 'Not Approved' : 'Pending Approval'}
                            </p>
                            <p className="text-xs text-zinc-600 mt-1" data-testid="approval-banner-message">
                                {isRejected
                                    ? <>Your account application was not approved.{user?.rejection_reason ? <> Reason: <span className="font-semibold">{user.rejection_reason}</span>.</> : null} If you believe this is an error, please <a href="/contact" className="underline font-semibold" data-testid="banner-contact-link">contact our support team</a>.</>
                                    : <>Our team is reviewing your registration and you will be notified once your account is approved. Once approved, you will have full access to browse verified workers and send job offers.</>}
                            </p>
                        </div>
                    </div>
                </div>
            )}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid grid-cols-5 w-full max-w-2xl rounded-none h-12 bg-zinc-100 mb-6">
                    <TabsTrigger value="talent" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="talent-pool-tab">
                        <Users className="w-4 h-4 mr-1" /> Talent
                    </TabsTrigger>
                    <TabsTrigger value="posted-jobs" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="posted-jobs-tab">
                        <Briefcase className="w-4 h-4 mr-1" /> Posted Jobs
                    </TabsTrigger>
                    <TabsTrigger value="candidates" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="candidates-tab">
                        <Send className="w-4 h-4 mr-1" /> Offers
                    </TabsTrigger>
                    <TabsTrigger value="interviews" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="interviews-tab">
                        <Calendar className="w-4 h-4 mr-1" /> Interviews
                    </TabsTrigger>
                    <TabsTrigger value="travel" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="travel-tab">
                        <Calendar className="w-4 h-4 mr-1" /> Travel
                    </TabsTrigger>
                </TabsList>

                {/* TALENT POOL */}
                <TabsContent value="talent">
                    {!isApproved ? (
                        <div className="border-2 border-zinc-900 p-8 text-center" data-testid="talent-pool-blocked">
                            <Users className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                            <p className="font-heading font-bold text-lg mb-2" data-testid="talent-pool-blocked-title">
                                {isRejected ? 'Talent pool unavailable' : 'Account pending approval'}
                            </p>
                            <p className="text-sm text-zinc-600 max-w-lg mx-auto" data-testid="talent-pool-blocked-message">
                                {isRejected
                                    ? <>Your account was not approved, so the talent pool is unavailable. <a href="/contact" className="underline font-semibold" data-testid="blocked-contact-link">Contact our support team</a> for assistance.</>
                                    : 'Your account is pending approval. Our team is reviewing your registration and you will be notified once your account is approved. Once approved, you will have full access to browse verified workers and send job offers.'}
                            </p>
                        </div>
                    ) : (<>
                    <div className="space-y-3 mb-6" data-testid="talent-filters">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input placeholder="Search workers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-12 rounded-none" data-testid="search-workers-input" /></div>
                            <MultiSelectFilter
                                label="Interest"
                                options={INTEREST_FILTERS}
                                selected={interestFilter}
                                onChange={setInterestFilter}
                                allLabel="All Interests"
                                triggerClassName="w-full sm:w-48 h-12"
                                testId="interest-filter"
                            />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <MultiSelectFilter
                                label="Country"
                                options={WORKER_COUNTRIES}
                                selected={countryFilter}
                                onChange={setCountryFilter}
                                allLabel="All Countries"
                                triggerClassName="w-full sm:w-48 h-11"
                                testId="country-filter"
                            />
                            <Select value={dateSortOrder} onValueChange={setDateSortOrder}><SelectTrigger className="w-full sm:w-56 h-11 rounded-none" data-testid="date-sort-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="newest">Date Joined: Newest → Oldest</SelectItem><SelectItem value="oldest">Date Joined: Oldest → Newest</SelectItem></SelectContent></Select>
                            {hasActiveFilters && (
                                <Button variant="outline" className="h-11 rounded-none sm:ml-auto" onClick={clearFilters} data-testid="clear-filters-btn">
                                    <X className="w-4 h-4 mr-1" /> Clear filters
                                </Button>
                            )}
                        </div>
                    </div>
                    {filteredWorkers.length === 0 ? (
                        <div className="text-center py-12 border border-zinc-200" data-testid="talent-pool-empty">
                            <Users className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                            <p className="font-heading font-bold text-base mb-1">{hasActiveFilters ? 'No workers match your filters' : 'No verified workers found'}</p>
                            <p className="text-sm text-zinc-500 max-w-md mx-auto">
                                {hasActiveFilters ? 'No workers match your current filters. Try adjusting your search or filter criteria.' : 'Workers will appear here as they complete their profiles and become verified.'}
                            </p>
                            {hasActiveFilters && <Button variant="outline" className="rounded-none mt-4" onClick={clearFilters} data-testid="empty-clear-filters-btn">Clear filters</Button>}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="workers-grid">
                            {filteredWorkers.map(w => (
                                <div key={w.id} className="border border-zinc-200 p-5 hover:border-zinc-800 transition-colors" data-testid={`worker-card-${w.id}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        {cardPhotos[w.id] ? (
                                            <img src={cardPhotos[w.id]} alt={w.name} className="w-12 h-12 object-cover flex-shrink-0 border border-zinc-200" />
                                        ) : (
                                            <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center flex-shrink-0"><Users className="w-6 h-6 text-zinc-400" /></div>
                                        )}
                                        <div><h4 className="font-semibold text-sm">{w.name}{calcAge(w.dob) && <span className="text-zinc-400 font-normal"> · {calcAge(w.dob)}</span>}</h4><div className="flex items-center gap-1 text-xs text-zinc-500"><MapPin className="w-3 h-3" />{w.country}</div></div>
                                    </div>
                                    <p className="text-[10px] tracking-[0.1em] uppercase text-zinc-400 mb-2" data-testid={`worker-last-active-${w.id}`}>{relativeLastActive(w.last_login, w.created_at)}</p>
                                    <div className="flex flex-wrap gap-1.5 mb-3">{(w.interests || w.skills || []).slice(0, 3).map(s => <Badge key={s} variant="secondary" className="rounded-none text-xs">{s}</Badge>)}{(w.interests || w.skills || []).length > 3 && <Badge variant="secondary" className="rounded-none text-xs">+{w.interests.length - 3}</Badge>}</div>
                                    {w.experience && <p className="text-xs text-zinc-500 mb-4 line-clamp-2">{w.experience}</p>}
                                    {/* Document verification badges — show only documents the worker actually has */}
                                    <VerifBadges worker={w} testIdPrefix={w.id} />
                                    <Button variant="outline" className="w-full h-10 rounded-none border-zinc-900 text-sm font-semibold" onClick={() => viewWorkerProfile(w.id)} data-testid={`view-profile-${w.id}`}><Eye className="w-4 h-4 mr-2" /> View Profile</Button>
                                </div>
                            ))}
                        </div>
                    )}
                    </>)}
                </TabsContent>

                {/* POSTED JOBS TAB */}
                <TabsContent value="posted-jobs">
                    <EmployerJobBoard
                        jobs={jobs}
                        onCreateJob={() => setShowJobForm(true)}
                        onEditJob={startEditJob}
                        onDeleteJob={deleteJob}
                        onRefresh={fetchData}
                        onOpenWorkerProfile={viewWorkerProfile}
                        onSendOfferToWorker={(workerId) => openWorkerAndAction(workerId, 'offer')}
                        onScheduleInterviewWithWorker={(workerId) => openWorkerAndAction(workerId, 'interview')}
                    />
                </TabsContent>

                {/* CANDIDATES/OFFERS TAB */}
                <TabsContent value="candidates">
                    {candidates.length === 0 ? (
                        <div className="text-center py-12 border border-zinc-200" data-testid="candidates-empty">
                            <Send className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                            <p className="font-heading font-bold text-base mb-1">No candidates yet</p>
                            <p className="text-sm text-zinc-500 max-w-md mx-auto">No candidates yet. Browse the talent pool and send offers to workers you are interested in.</p>
                        </div>
                    ) : (() => {
                        let list = candidates.filter(c => !candidateSearch || (c.worker_name || '').toLowerCase().includes(candidateSearch.toLowerCase()));
                        if (candidateSort === 'accepted') list = list.filter(c => c.status === 'accepted');
                        else if (candidateSort === 'rejected') list = list.filter(c => c.status === 'rejected');
                        else if (candidateSort === 'pending') list = list.filter(c => c.status === 'pending');
                        if (candidateSort === 'recent') list = [...list].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
                        else if (candidateSort === 'oldest') list = [...list].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
                        return (
                            <>
                                <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="candidate-controls">
                                    <div className="relative flex-1">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                                        <Input placeholder="Search candidates by name..." value={candidateSearch} onChange={e => setCandidateSearch(e.target.value)} className="pl-10 h-11 rounded-none" data-testid="candidate-search-input" />
                                    </div>
                                    <Select value={candidateSort} onValueChange={setCandidateSort}>
                                        <SelectTrigger className="w-full sm:w-56 h-11 rounded-none" data-testid="candidate-sort-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="recent">Most Recent first</SelectItem>
                                            <SelectItem value="oldest">Oldest first</SelectItem>
                                            <SelectItem value="accepted">Offers Accepted</SelectItem>
                                            <SelectItem value="rejected">Offers Rejected</SelectItem>
                                            <SelectItem value="pending">Offers Pending</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {list.length === 0 ? (
                                    <div className="text-center py-12 border border-zinc-200" data-testid="candidates-no-results">
                                        <p className="text-sm text-zinc-500">No candidates match your search or filter.</p>
                                    </div>
                                ) : (
                                    <div className="border border-zinc-200 overflow-x-auto" data-testid="candidates-table">
                                        <Table>
                                            <TableHeader><TableRow>
                                                <TableHead className="font-bold">Worker</TableHead>
                                                <TableHead className="font-bold">Position</TableHead>
                                                <TableHead className="font-bold">Status</TableHead>
                                                <TableHead className="font-bold">Visa</TableHead>
                                                <TableHead className="font-bold">Actions</TableHead>
                                            </TableRow></TableHeader>
                                            <TableBody>
                                                {list.map(c => (
                                                    <TableRow key={c.id} data-testid={`candidate-row-${c.id}`}>
                                                        <TableCell><span className="font-medium text-sm">{c.worker_name}</span></TableCell>
                                                        <TableCell className="text-sm">{c.title}</TableCell>
                                                        <TableCell><Badge variant={c.status === 'accepted' ? 'default' : c.status === 'rejected' ? 'destructive' : 'outline'} className="rounded-none text-xs">{c.status}</Badge></TableCell>
                                                        <TableCell><Badge variant="outline" className="rounded-none text-xs capitalize">{c.visa_status}</Badge></TableCell>
                                                        <TableCell>
                                                            {c.status === 'accepted' && (
                                                                <div className="flex gap-1 flex-wrap">
                                                                    <Button size="sm" variant="outline" className="rounded-none text-xs h-8" onClick={() => { setSelectedCandidate(c); setShowVisaDialog(true); }} data-testid={`manage-visa-${c.id}`}>Manage</Button>
                                                                    <Button size="sm" variant="ghost" className="rounded-none text-xs h-8" onClick={() => openAcceptedDocs(c.worker_id)} data-testid={`view-accepted-docs-${c.id}`}>
                                                                        <FileText className="w-3 h-3 mr-1" /> Docs
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </TabsContent>

                {/* INTERVIEWS TAB */}
                <TabsContent value="interviews">
                    <EmployerInterviewsTab
                        interviews={interviews}
                        interviewPhotos={interviewPhotos}
                        cardPhotos={cardPhotos}
                        resolveStatus={resolveInterviewStatus}
                        onReschedule={openReschedule}
                        onCancel={(iv) => setCancelTarget(iv)}
                        showPast={showPastInterviews}
                        setShowPast={setShowPastInterviews}
                        successMsg={interviewsTabSuccess}
                    />
                </TabsContent>

                {/* TRAVEL TRACKING TAB */}
                <TabsContent value="travel">
                    {(() => {
                        const accepted = candidates.filter(c => c.status === 'accepted');
                        if (accepted.length === 0) return (
                            <div className="text-center py-12 border border-zinc-200" data-testid="travel-tracker-empty">
                                <Calendar className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                                <p className="font-heading font-bold text-base mb-1">No travel records yet</p>
                                <p className="text-sm text-zinc-500 max-w-md mx-auto">No workers in the travel stage yet. Travel dates will appear here once workers have accepted offers and visa documents are uploaded.</p>
                            </div>
                        );
                        const sorted = [...accepted].sort((a, b) => {
                            const aHasDate = !!a.travel_date;
                            const bHasDate = !!b.travel_date;
                            if (travelSort === 'soonest') {
                                if (!aHasDate && !bHasDate) return 0;
                                if (!aHasDate) return 1;
                                if (!bHasDate) return -1;
                                return a.travel_date.localeCompare(b.travel_date);
                            }
                            if (travelSort === 'latest') {
                                if (!aHasDate && !bHasDate) return 0;
                                if (!aHasDate) return 1;
                                if (!bHasDate) return -1;
                                return b.travel_date.localeCompare(a.travel_date);
                            }
                            if (travelSort === 'name') return (a.worker_name || '').localeCompare(b.worker_name || '');
                            if (travelSort === 'visa-status') return (a.visa_status || '').localeCompare(b.visa_status || '');
                            return 0;
                        });
                        return (
                            <>
                                <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="travel-sort-controls">
                                    <Select value={travelSort} onValueChange={setTravelSort}>
                                        <SelectTrigger className="w-full sm:w-72 h-11 rounded-none" data-testid="travel-sort-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="soonest">Travel Date: Soonest first</SelectItem>
                                            <SelectItem value="latest">Travel Date: Latest first</SelectItem>
                                            <SelectItem value="name">Worker Name (A-Z)</SelectItem>
                                            <SelectItem value="visa-status">Visa Status</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="border border-zinc-200 overflow-x-auto" data-testid="travel-tracker-table">
                                    <Table>
                                        <TableHeader><TableRow>
                                            <TableHead className="font-bold">Worker</TableHead>
                                            <TableHead className="font-bold">Position</TableHead>
                                            <TableHead className="font-bold">Visa Status</TableHead>
                                            <TableHead className="font-bold">Travel Date</TableHead>
                                            <TableHead className="font-bold">Docs</TableHead>
                                        </TableRow></TableHeader>
                                        <TableBody>
                                            {sorted.map(c => (
                                                <TableRow key={c.id} className="cursor-pointer hover:bg-zinc-50" onClick={() => { setSelectedCandidate(c); setShowVisaDialog(true); }} data-testid={`travel-row-${c.id}`}>
                                                    <TableCell className="font-medium text-sm">{c.worker_name}</TableCell>
                                                    <TableCell className="text-sm">{c.title}</TableCell>
                                                    <TableCell><Badge variant="outline" className="rounded-none text-xs capitalize">{c.visa_status}</Badge></TableCell>
                                                    <TableCell className="text-sm">{c.travel_date ? new Date(c.travel_date + 'T00:00:00').toLocaleDateString('en-US', {month:'short',day:'numeric',year:'numeric'}) : <span className="text-zinc-400" data-testid={`travel-not-set-${c.id}`}>Not set</span>}</TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-1">
                                                            {c.visa_doc_id ? <Check className="w-4 h-4 text-green-600" /> : <span className="text-xs text-zinc-400">No visa</span>}
                                                            {c.ticket_doc_id ? <Check className="w-4 h-4 text-green-600" /> : <span className="text-xs text-zinc-400">No ticket</span>}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </>
                        );
                    })()}
                </TabsContent>
            </Tabs>

            {/* Email Preferences — opt out of categories of transactional emails. */}
            <div className="mt-8">
                <EmailPreferencesPanel />
            </div>

            {/* Edit Job Dialog */}
            <Dialog open={showEditJob} onOpenChange={setShowEditJob}>
                <DialogContent className="max-w-md rounded-none border-2 border-zinc-900 max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-xl">Edit Job</DialogTitle><DialogDescription>Changes apply to future offers only.</DialogDescription></DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div><Label className="text-sm font-semibold">Job Title</Label><Input value={editJobData.title || ''} onChange={e => setEditJobData(d => ({...d, title: e.target.value}))} className="mt-1.5 h-12 rounded-none" data-testid="edit-job-title" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-sm font-semibold">Salary</Label><Input value={editJobData.salary || ''} onChange={e => setEditJobData(d => ({...d, salary: e.target.value}))} className="mt-1.5 h-12 rounded-none" data-testid="edit-job-salary" /></div>
                            <div><Label className="text-sm font-semibold">Currency</Label>
                                <Select value={editJobData.currency || 'USD'} onValueChange={v => setEditJobData(d => ({ ...d, currency: v, country: CURRENCY_TO_COUNTRY[v] || d.country }))}><SelectTrigger className="mt-1.5 h-12 rounded-none" data-testid="edit-job-currency"><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="AED">AED</SelectItem><SelectItem value="SAR">SAR</SelectItem><SelectItem value="QAR">QAR</SelectItem><SelectItem value="KWD">KWD</SelectItem><SelectItem value="BHD">BHD</SelectItem><SelectItem value="OMR">OMR</SelectItem></SelectContent></Select></div>
                        </div>
                        <div><Label className="text-sm font-semibold">Benefits</Label><Input value={editJobData.benefits || ''} onChange={e => setEditJobData(d => ({...d, benefits: e.target.value}))} className="mt-1.5 h-12 rounded-none" data-testid="edit-job-benefits" /></div>
                        <div><Label className="text-sm font-semibold">Location</Label><Input value={editJobData.location || ''} onChange={e => setEditJobData(d => ({...d, location: e.target.value}))} className="mt-1.5 h-12 rounded-none" data-testid="edit-job-location" /></div>
                        <div><Label className="text-sm font-semibold">Country</Label>
                            <Select value={editJobData.country || 'UAE'} onValueChange={v => setEditJobData(d => ({ ...d, country: v, currency: COUNTRY_TO_CURRENCY[v] || 'USD' }))}><SelectTrigger className="mt-1.5 h-12 rounded-none" data-testid="edit-job-country"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="UAE">UAE</SelectItem><SelectItem value="Saudi Arabia">Saudi Arabia</SelectItem><SelectItem value="Qatar">Qatar</SelectItem><SelectItem value="Kuwait">Kuwait</SelectItem><SelectItem value="Bahrain">Bahrain</SelectItem><SelectItem value="Oman">Oman</SelectItem></SelectContent></Select></div>
                        <div><Label className="text-sm font-semibold">Description</Label><Textarea value={editJobData.description || ''} onChange={e => setEditJobData(d => ({...d, description: e.target.value}))} className="mt-1.5 rounded-none min-h-[80px]" data-testid="edit-job-description" /></div>
                        <div>
                            <Label className="text-sm font-semibold">Visibility</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="edit-job-visibility-toggle">
                                <button
                                    type="button"
                                    onClick={() => setEditJobData(d => ({ ...d, visibility: 'public' }))}
                                    className={`h-12 border text-sm font-semibold transition-colors ${(editJobData.visibility || 'public') === 'public' ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}
                                    data-testid="edit-job-visibility-public"
                                >
                                    Public
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEditJobData(d => ({ ...d, visibility: 'private' }))}
                                    className={`h-12 border text-sm font-semibold transition-colors ${editJobData.visibility === 'private' ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}
                                    data-testid="edit-job-visibility-private"
                                >
                                    Private
                                </button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowEditJob(false)} className="rounded-none">Cancel</Button>
                        <Button onClick={saveEditJob} disabled={savingJob} className="rounded-none bg-zinc-900 text-white font-semibold" data-testid="save-edit-job-btn">{savingJob ? 'Saving...' : 'Save Changes'}</Button>
                    </DialogFooter>
                    <p className="text-xs text-zinc-400 mt-2">Job updated. This will apply to future offers only.</p>
                </DialogContent>
            </Dialog>

            {/* Worker Profile Dialog (Layer 2) */}
            <Dialog open={showProfile} onOpenChange={(o) => {
                // While a child form (Schedule Interview / Send Offer) or the
                // document preview is open, ignore close attempts on the profile.
                // This prevents Radix's auto-close-on-overlay-click from
                // collapsing the profile when only a child layer should close.
                if (!o && (previewDoc || showInterviewForm || showOfferForm || rescheduleTarget || cancelTarget)) return;
                setShowProfile(o);
                if (!o) {
                    setWorkerPhotoBlobUrl(null);
                    setProfileSuccessMsg('');
                    clearProfileSuccessTimer();
                }
            }}>
                <DialogContent
                    className="max-w-lg rounded-none border-2 border-zinc-900 max-h-[85vh] overflow-y-auto"
                >
                    {selectedWorker && (<>
                        <DialogHeader>
                            <div className="flex items-center gap-4">
                                {workerPhotoBlobUrl ? (
                                    <img src={workerPhotoBlobUrl} alt={selectedWorker.name} className="w-16 h-16 object-cover border border-zinc-200" data-testid="worker-photo" />
                                ) : (
                                    <div className="w-16 h-16 bg-zinc-100 flex items-center justify-center"><Users className="w-8 h-8 text-zinc-400" /></div>
                                )}
                                <div>
                                    <DialogTitle className="font-heading text-xl">{selectedWorker.name}{calcAge(selectedWorker.dob) && <span className="text-zinc-400 font-normal text-base ml-2">{calcAge(selectedWorker.dob)} years old</span>}</DialogTitle>
                                    <p className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 mt-1" data-testid="profile-last-active">{relativeLastActive(selectedWorker.last_login, selectedWorker.created_at)}</p>
                                    <DialogDescription className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {selectedWorker.country}</DialogDescription>
                                </div>
                            </div>
                        </DialogHeader>
                        {profileSuccessMsg && (
                            <div className="mt-3 bg-green-50 border border-green-200 p-2 text-sm text-green-700 flex items-center gap-2" data-testid="profile-success-msg">
                                <Check className="w-4 h-4" /> {profileSuccessMsg}
                            </div>
                        )}
                        <div className="space-y-4 mt-4">
                            <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Interests</p><div className="flex flex-wrap gap-2">{(selectedWorker.interests || selectedWorker.skills || []).map(s => <Badge key={s} variant="outline" className="rounded-none">{s}</Badge>)}</div></div>
                            <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Languages</p><div className="flex flex-wrap gap-2">{(selectedWorker.languages || []).map(l => <Badge key={l} variant="secondary" className="rounded-none">{l}</Badge>)}</div></div>
                            {selectedWorker.experience && <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Experience</p><p className="text-sm">{selectedWorker.experience}</p></div>}
                            {/* Scheduled Interview banner — shown only if an interview exists for this worker */}
                            {(() => {
                                const workerInterviews = interviews
                                    .filter(iv => iv.worker_id === selectedWorker.id)
                                    .sort((a, b) => (`${b.date}T${b.time}`).localeCompare(`${a.date}T${a.time}`));
                                if (workerInterviews.length === 0) return null;
                                const iv = workerInterviews[0]; // most relevant: most recent
                                const s = resolveInterviewStatus(iv);
                                const isActionable = s === 'upcoming' || s === 'live';
                                const statusBadge = {
                                    upcoming: { label: 'Upcoming', cls: 'text-blue-700 border-blue-300 bg-blue-50' },
                                    live: { label: 'Live Now', cls: 'text-green-700 border-green-400 bg-green-50' },
                                    completed: { label: 'Completed', cls: 'text-green-700 border-green-300' },
                                    missed: { label: 'Missed', cls: 'text-red-600 border-red-300' },
                                    cancelled: { label: 'Cancelled', cls: 'text-zinc-500 border-zinc-300' },
                                }[s];
                                return (
                                    <div data-testid={`profile-interview-banner-${iv.id}`}>
                                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Scheduled Interview</p>
                                        <div className="border border-zinc-200 p-3 bg-zinc-50">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3 text-sm text-zinc-700">
                                                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {iv.date}</span>
                                                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {iv.time}</span>
                                                </div>
                                                <Badge variant="outline" className={`rounded-none text-xs ${statusBadge.cls}`} data-testid={`profile-interview-status-${iv.id}`}>{statusBadge.label}</Badge>
                                            </div>
                                            {iv.meet_link && (
                                                <a href={iv.meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:underline mb-2" data-testid={`profile-interview-link-${iv.id}`}>
                                                    <ExternalLink className="w-3 h-3" /> {iv.meet_link.length > 50 ? `${iv.meet_link.slice(0, 50)}...` : iv.meet_link}
                                                </a>
                                            )}
                                            {isActionable && (
                                                <div className="flex gap-2 mt-2">
                                                    <Button size="sm" variant="outline" className="rounded-none text-xs h-8 border-zinc-900" onClick={() => openReschedule(iv)} data-testid={`profile-reschedule-${iv.id}`}>
                                                        Reschedule
                                                    </Button>
                                                    <Button size="sm" variant="outline" className="rounded-none text-xs h-8 border-red-500 text-red-600" onClick={() => setCancelTarget(iv)} data-testid={`profile-cancel-interview-${iv.id}`}>
                                                        Cancel
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}
                            {selectedWorker?.video_intro_url && (
                                <>
                                    <Separator />
                                    <VideoIntroPlayer
                                        url={selectedWorker.video_intro_url}
                                        platform={selectedWorker.video_intro_platform}
                                        testIdPrefix="employer-profile-video"
                                    />
                                </>
                            )}
                            <Separator />
                            <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Documents</p>
                                <div className="space-y-2">{workerDocs.filter(d => d.doc_type !== 'photo' && d.doc_type !== 'additional').map(doc => (
                                    <div key={doc.id} className="flex items-center justify-between p-3 border border-zinc-200">
                                        <div className="flex items-center gap-2">
                                            {doc.doc_type === 'video_intro' ? <Video className="w-4 h-4 text-zinc-500" /> : doc.doc_type === 'interpol' ? <Shield className="w-4 h-4 text-zinc-500" /> : <FileText className="w-4 h-4 text-zinc-500" />}
                                            <span className="text-sm capitalize">{getDocLabel(doc)}</span>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-none text-xs h-7 border-zinc-900"
                                            onClick={() => setPreviewDoc(doc)}
                                            data-testid={`view-doc-${doc.id}`}
                                        >
                                            <Eye className="w-3 h-3 mr-1" /> View
                                        </Button>
                                    </div>
                                ))}</div>
                            </div>
                            {workerDocs.filter(d => d.doc_type === 'additional').length > 0 && (
                                <div data-testid="employer-additional-docs"><p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Additional Documents</p>
                                    <div className="space-y-2">{workerDocs.filter(d => d.doc_type === 'additional').map(doc => (
                                        <div key={doc.id} className="flex items-center justify-between p-3 border border-zinc-200" data-testid={`employer-additional-doc-${doc.id}`}>
                                            <div className="flex items-center gap-2 min-w-0">
                                                <FileText className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                                                <span className="text-sm truncate">{doc.doc_label || 'Additional document'}</span>
                                            </div>
                                            <Button size="sm" variant="outline" className="rounded-none text-xs h-7 border-zinc-900" onClick={() => setPreviewDoc(doc)} data-testid={`view-additional-doc-${doc.id}`}>
                                                <Eye className="w-3 h-3 mr-1" /> View
                                            </Button>
                                        </div>
                                    ))}</div>
                                </div>
                            )}
                        </div>
                        <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
                            <Button variant="outline" className="flex-1 h-12 rounded-none border-zinc-900 font-semibold" onClick={() => setShowInterviewForm(true)} data-testid="schedule-interview-btn">
                                <Calendar className="w-4 h-4 mr-2" /> Schedule Interview
                            </Button>
                            <Button
                                className="flex-1 h-12 rounded-none bg-zinc-900 text-white font-semibold disabled:opacity-50"
                                onClick={() => setShowOfferForm(true)}
                                disabled={!isApproved}
                                title={!isApproved ? 'Account approval required to send offers' : ''}
                                data-testid="send-offer-btn"
                            >
                                <Send className="w-4 h-4 mr-2" /> Send Offer
                            </Button>
                        </DialogFooter>
                    </>)}
                </DialogContent>
            </Dialog>

            {/* Send Offer Dialog (Layer 3 — sits on top of worker profile) */}
            <Dialog open={showOfferForm} onOpenChange={setShowOfferForm}>
                <DialogContent className="max-w-md rounded-none border-2 border-zinc-900 z-[60]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Send Job Offer</DialogTitle>
                        <DialogDescription>to {selectedWorker?.name}</DialogDescription>
                    </DialogHeader>
                    {activeJobs.length === 0 ? (
                        <div className="py-6 text-center">
                            <Briefcase className="w-10 h-10 text-zinc-200 mx-auto mb-3" />
                            <p className="text-sm text-zinc-500 mb-4">Create a job listing first to send offers.</p>
                            <Button className="rounded-none bg-zinc-900 text-white" onClick={() => { setShowOfferForm(false); setShowJobForm(true); }} data-testid="create-job-from-offer-btn"><Plus className="w-4 h-4 mr-2" /> Create Job</Button>
                        </div>
                    ) : (
                        <div className="space-y-4 mt-4">
                            <div>
                                <Label className="text-sm font-semibold">Select Job</Label>
                                <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                                    <SelectTrigger className="mt-1.5 h-12 rounded-none" data-testid="select-job-for-offer"><SelectValue placeholder="Choose a job listing" /></SelectTrigger>
                                    <SelectContent className="z-[70]">{activeJobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title} - {j.location}, {j.country}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            {selectedJobId && (() => {
                                const job = activeJobs.find(j => j.id === selectedJobId);
                                return job ? (
                                    <div className="border border-zinc-200 p-4 bg-zinc-50 space-y-2" data-testid="selected-job-preview">
                                        <p className="font-semibold">{job.title}</p>
                                        <p className="text-sm text-zinc-500">{job.salary} | {job.location}, {job.country}</p>
                                        {job.benefits && <p className="text-xs text-zinc-500">Benefits: {job.benefits}</p>}
                                        {job.description && <p className="text-sm text-zinc-600 mt-1">{job.description}</p>}
                                    </div>
                                ) : null;
                            })()}
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowOfferForm(false)} className="rounded-none">Cancel</Button>
                                <Button onClick={sendOffer} disabled={sending || !selectedJobId} className="rounded-none bg-zinc-900 text-white font-semibold" data-testid="confirm-send-offer-btn">{sending ? 'Sending...' : 'Send Offer'}</Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Create Job Dialog */}
            <Dialog open={showJobForm} onOpenChange={setShowJobForm}>
                <DialogContent className="max-w-md rounded-none border-2 border-zinc-900 max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-xl">Create Job Listing</DialogTitle><DialogDescription>Fill in the details once, send to multiple candidates</DialogDescription></DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div><Label className="text-sm font-semibold">Job Title</Label><Input value={jobTitle} onChange={e => setJobTitle(e.target.value)} className="mt-1.5 h-12 rounded-none" placeholder="e.g., Security Guard" data-testid="job-title-input" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-sm font-semibold">Monthly Salary</Label><Input value={jobSalary} onChange={e => setJobSalary(e.target.value)} className="mt-1.5 h-12 rounded-none" placeholder="e.g., 2500" data-testid="job-salary-input" /></div>
                            <div><Label className="text-sm font-semibold">Currency</Label>
                                <Select value={jobCurrency} onValueChange={v => { setJobCurrency(v); if (CURRENCY_TO_COUNTRY[v]) setJobCountry(CURRENCY_TO_COUNTRY[v]); }}><SelectTrigger className="mt-1.5 h-12 rounded-none" data-testid="job-currency-select"><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="AED">AED</SelectItem><SelectItem value="SAR">SAR</SelectItem><SelectItem value="QAR">QAR</SelectItem><SelectItem value="KWD">KWD</SelectItem><SelectItem value="BHD">BHD</SelectItem><SelectItem value="OMR">OMR</SelectItem></SelectContent></Select></div>
                        </div>
                        <div><Label className="text-sm font-semibold">Benefits</Label><Input value={jobBenefits} onChange={e => setJobBenefits(e.target.value)} className="mt-1.5 h-12 rounded-none" placeholder="e.g., Housing, meals, transport" data-testid="job-benefits-input" /></div>
                        <div><Label className="text-sm font-semibold">Location</Label><Input value={jobLocation} onChange={e => setJobLocation(e.target.value)} className="mt-1.5 h-12 rounded-none" placeholder="e.g., Dubai" data-testid="job-location-input" /></div>
                        <div><Label className="text-sm font-semibold">Country</Label>
                            <Select value={jobCountry} onValueChange={v => { setJobCountry(v); setJobCurrency(COUNTRY_TO_CURRENCY[v] || 'USD'); }}><SelectTrigger className="mt-1.5 h-12 rounded-none" data-testid="job-country-select"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="UAE">UAE</SelectItem><SelectItem value="Saudi Arabia">Saudi Arabia</SelectItem><SelectItem value="Qatar">Qatar</SelectItem><SelectItem value="Kuwait">Kuwait</SelectItem><SelectItem value="Bahrain">Bahrain</SelectItem><SelectItem value="Oman">Oman</SelectItem></SelectContent></Select></div>
                        <div><Label className="text-sm font-semibold">Job Description</Label><Textarea value={jobDescription} onChange={e => setJobDescription(e.target.value)} className="mt-1.5 rounded-none min-h-[100px]" placeholder="Describe the role, responsibilities, requirements..." data-testid="job-description-input" /></div>
                        <div>
                            <Label className="text-sm font-semibold">Visibility</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="job-visibility-toggle">
                                <button
                                    type="button"
                                    onClick={() => setJobVisibility('public')}
                                    className={`h-12 border text-sm font-semibold transition-colors ${jobVisibility === 'public' ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}
                                    data-testid="job-visibility-public"
                                >
                                    Public
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setJobVisibility('private')}
                                    className={`h-12 border text-sm font-semibold transition-colors ${jobVisibility === 'private' ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}
                                    data-testid="job-visibility-private"
                                >
                                    Private
                                </button>
                            </div>
                            <p className="text-xs text-zinc-500 mt-2">
                                {jobVisibility === 'public'
                                    ? 'Public jobs appear in the Job Board where workers can browse and apply.'
                                    : 'Private jobs are only visible when you send direct offers from the talent pool.'}
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowJobForm(false)} className="rounded-none">Cancel</Button>
                        <Button onClick={createJob} disabled={savingJob || !jobTitle || !jobSalary || !jobLocation || !jobDescription} className="rounded-none bg-zinc-900 text-white font-semibold" data-testid="save-job-btn">{savingJob ? 'Creating...' : 'Create Job'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Schedule Interview Dialog (Layer 3 — sits on top of worker profile) */}
            <Dialog open={showInterviewForm} onOpenChange={setShowInterviewForm}>
                <DialogContent className="max-w-md rounded-none border-2 border-zinc-900 z-[60]">
                    <DialogHeader><DialogTitle className="font-heading text-xl">Schedule Interview</DialogTitle><DialogDescription>with {selectedWorker?.name}</DialogDescription></DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div><Label className="text-sm font-semibold">Date</Label><Input type="date" value={interviewDate} onChange={e => setInterviewDate(e.target.value)} className="mt-1.5 h-12 rounded-none" data-testid="interview-date-input" /></div>
                        <div><Label className="text-sm font-semibold">Time</Label><Input type="time" value={interviewTime} onChange={e => setInterviewTime(e.target.value)} className="mt-1.5 h-12 rounded-none" data-testid="interview-time-input" /></div>
                        <div>
                            <Label className="text-sm font-semibold">Meet Link</Label>
                            <Input
                                value={interviewMeet}
                                onChange={e => setInterviewMeet(e.target.value)}
                                className="mt-1.5 h-12 rounded-none"
                                placeholder="Paste your meeting link here (Google Meet, Zoom, Teams, etc.)"
                                data-testid="interview-meet-input"
                            />
                            <p className="text-[11px] text-zinc-400 mt-1">Any video meeting platform is accepted.</p>
                        </div>
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowInterviewForm(false)} className="rounded-none">Cancel</Button>
                        <Button onClick={scheduleInterview} disabled={schedulingInterview || !interviewDate || !interviewTime || !interviewMeet} className="rounded-none bg-zinc-900 text-white font-semibold" data-testid="confirm-interview-btn">{schedulingInterview ? 'Scheduling...' : 'Schedule Interview'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reschedule Interview Dialog (Layer 3 — sits on top of profile/interviews tab) */}
            <Dialog open={!!rescheduleTarget} onOpenChange={(o) => { if (!o) setRescheduleTarget(null); }}>
                <DialogContent className="max-w-md rounded-none border-2 border-zinc-900 z-[60]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Reschedule Interview</DialogTitle>
                        <DialogDescription>with {rescheduleTarget?.worker_name}</DialogDescription>
                    </DialogHeader>
                    {rescheduleTarget && (
                        <div className="space-y-4 mt-4" data-testid="reschedule-form">
                            <div className="bg-zinc-50 border border-zinc-200 p-2 text-xs text-zinc-500">
                                Current: <span className="font-semibold text-zinc-700">{rescheduleTarget.date} at {rescheduleTarget.time}</span>
                            </div>
                            <div>
                                <Label className="text-sm font-semibold">New Date</Label>
                                <Input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} className="mt-1.5 h-12 rounded-none" data-testid="reschedule-date-input" />
                            </div>
                            <div>
                                <Label className="text-sm font-semibold">New Time</Label>
                                <Input type="time" value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)} className="mt-1.5 h-12 rounded-none" data-testid="reschedule-time-input" />
                            </div>
                            <div>
                                <Label className="text-sm font-semibold">Meet Link</Label>
                                <Input
                                    value={rescheduleMeet}
                                    onChange={e => setRescheduleMeet(e.target.value)}
                                    className="mt-1.5 h-12 rounded-none"
                                    placeholder="Paste your meeting link here"
                                    data-testid="reschedule-meet-input"
                                />
                                <p className="text-[11px] text-zinc-400 mt-1">You can edit the link or keep the existing one.</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setRescheduleTarget(null)} className="rounded-none" data-testid="reschedule-cancel-btn">Cancel</Button>
                        <Button onClick={submitReschedule} disabled={savingReschedule || !rescheduleDate || !rescheduleTime || !rescheduleMeet} className="rounded-none bg-zinc-900 text-white font-semibold" data-testid="reschedule-save-btn">
                            {savingReschedule ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel Interview Confirmation Dialog */}
            <AlertDialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) setCancelTarget(null); }}>
                <AlertDialogContent className="rounded-none border-2 border-zinc-900 z-[60]">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading text-xl">Cancel Interview?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to cancel this interview with <strong>{cancelTarget?.worker_name}</strong> scheduled for {cancelTarget?.date} at {cancelTarget?.time}? The worker will be notified.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" data-testid="cancel-interview-keep">Keep Interview</AlertDialogCancel>
                        <AlertDialogAction onClick={submitCancelInterview} disabled={cancellingInterview} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-interview-confirm">
                            {cancellingInterview ? 'Cancelling...' : 'Cancel Interview'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Visa Management Dialog */}
            <Dialog open={showVisaDialog} onOpenChange={(o) => { if (!o && previewDoc) return; setShowVisaDialog(o); }}>
                <DialogContent
                    className="max-w-md rounded-none border-2 border-zinc-900 max-h-[85vh] overflow-y-auto"
                >
                    {selectedCandidate && (<>
                        <DialogHeader><DialogTitle className="font-heading text-xl">Manage Visa</DialogTitle><DialogDescription>{selectedCandidate.worker_name} - {selectedCandidate.title}</DialogDescription></DialogHeader>
                        <VisaManager candidate={selectedCandidate} onUpdate={async () => { await fetchData(); }} api={api} onPreview={setPreviewDoc} />
                    </>)}
                </DialogContent>
            </Dialog>

            {/* Universal Document Preview Modal (Fix 5) */}
            <DocumentPreviewModal
                open={!!previewDoc}
                onClose={() => setPreviewDoc(null)}
                doc={previewDoc}
            />

            {/* Accepted-Candidate Documents Dialog (Fix 6 — read-only) */}
            <Dialog open={showAcceptedDocs} onOpenChange={(o) => { if (!o && previewDoc) return; setShowAcceptedDocs(o); }}>
                <DialogContent className="max-w-lg rounded-none border-2 border-zinc-900 max-h-[85vh] overflow-y-auto" data-testid="accepted-docs-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Worker Documents</DialogTitle>
                        <DialogDescription>Read-only view of the worker&apos;s profile documents.</DialogDescription>
                    </DialogHeader>
                    {acceptedDocsLoading && (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent animate-spin" />
                        </div>
                    )}
                    {!acceptedDocsLoading && acceptedWorker && (
                        <div className="mt-2">
                            {/* Worker header */}
                            <div className="flex items-center gap-3 border border-zinc-200 p-3 mb-4">
                                {acceptedPhotoBlobUrl ? (
                                    <img src={acceptedPhotoBlobUrl} alt="" className="w-14 h-14 object-cover" data-testid="accepted-worker-photo" />
                                ) : (
                                    <div className="w-14 h-14 bg-zinc-100 flex items-center justify-center"><Users className="w-5 h-5 text-zinc-300" /></div>
                                )}
                                <div className="min-w-0">
                                    <p className="font-bold text-sm" data-testid="accepted-worker-name">{acceptedWorker.name}</p>
                                    <p className="text-xs text-zinc-500 truncate">{acceptedWorker.country || ''} · {acceptedWorker.skill || acceptedWorker.experience || ''}</p>
                                </div>
                            </div>

                            {/* Document list */}
                            <div className="space-y-2" data-testid="accepted-docs-list">
                                {[
                                    { type: 'passport', label: 'Passport' },
                                    { type: 'interpol', label: 'Interpol/Police Clearance' },
                                    { type: 'medical_report', label: 'Medical Report' },
                                    { type: 'video_intro', label: 'Video Introduction' },
                                ].map(({ type, label }) => {
                                    const doc = acceptedDocsList.find(d => d.doc_type === type);
                                    return (
                                        <div key={type} className="flex items-center justify-between p-3 border border-zinc-100" data-testid={`accepted-doc-row-${type}`}>
                                            <div className="flex items-center gap-2 min-w-0">
                                                <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-sm">{label}</p>
                                                    {doc ? (
                                                        <p className="text-[10px] text-zinc-400 truncate">{doc.original_filename}</p>
                                                    ) : (
                                                        <p className="text-[10px] text-zinc-300">Not uploaded</p>
                                                    )}
                                                </div>
                                            </div>
                                            {doc && (
                                                <Button size="sm" variant="ghost" className="rounded-none text-xs h-7" onClick={() => setPreviewDoc(doc)} data-testid={`accepted-doc-view-${type}`}>
                                                    <Eye className="w-3 h-3 mr-1" /> View
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                                {/* Additional docs (variable count) */}
                                {acceptedDocsList.filter(d => d.doc_type === 'additional').map(doc => (
                                    <div key={doc.id} className="flex items-center justify-between p-3 border border-zinc-100" data-testid={`accepted-doc-row-additional-${doc.id}`}>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm capitalize truncate">{doc.doc_label || 'Additional document'}</p>
                                                <p className="text-[10px] text-zinc-400 truncate">{doc.original_filename}</p>
                                            </div>
                                        </div>
                                        <Button size="sm" variant="ghost" className="rounded-none text-xs h-7" onClick={() => setPreviewDoc(doc)}>
                                            <Eye className="w-3 h-3 mr-1" /> View
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function VisaManager({ candidate, onUpdate, api, onPreview }) {
    const [status, setStatus] = useState(candidate.visa_status);
    const [travelDate, setTravelDate] = useState(candidate.travel_date || '');
    const [updating, setUpdating] = useState('');
    const [uploadingVisa, setUploadingVisa] = useState(false);
    const [uploadingTicket, setUploadingTicket] = useState(false);
    const [uploadingOfferLetter, setUploadingOfferLetter] = useState(false);
    const [visaUploaded, setVisaUploaded] = useState(!!candidate.visa_doc_id);
    const [ticketUploaded, setTicketUploaded] = useState(!!candidate.ticket_doc_id);
    const [offerLetterUploaded, setOfferLetterUploaded] = useState(!!candidate.offer_letter_doc_id);
    const [visaFilename, setVisaFilename] = useState('');
    const [ticketFilename, setTicketFilename] = useState('');
    const [offerLetterFilename, setOfferLetterFilename] = useState('');
    const [visaDocId, setVisaDocId] = useState(candidate.visa_doc_id || null);
    const [ticketDocId, setTicketDocId] = useState(candidate.ticket_doc_id || null);
    const [offerLetterDocId, setOfferLetterDocId] = useState(candidate.offer_letter_doc_id || null);
    const [successMsg, setSuccessMsg] = useState('');

    const updateStatus = async (newStatus) => {
        setUpdating(newStatus);
        setSuccessMsg('');
        try {
            const { data } = await api.put(`/employer/offers/${candidate.id}/visa-status`, { status: newStatus });
            setStatus(newStatus);
            if (data?.approved_pending_doc) {
                setSuccessMsg('Status saved as Approved. Upload at least one document to notify the worker.');
                setTimeout(() => setSuccessMsg(''), 6000);
            } else if (data?.notified) {
                setSuccessMsg(`Status updated to ${newStatus} — worker notified`);
                setTimeout(() => setSuccessMsg(''), 4000);
            } else {
                setSuccessMsg(`Status updated to ${newStatus}`);
                setTimeout(() => setSuccessMsg(''), 3000);
            }
            onUpdate();
        } catch (err) { console.error(err); }
        finally { setUpdating(''); }
    };

    const uploadDoc = async (file, docType) => {
        const setUploading = docType === 'visa' ? setUploadingVisa : docType === 'flight_ticket' ? setUploadingTicket : setUploadingOfferLetter;
        setUploading(true);
        setSuccessMsg('');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('doc_type', docType);
        if (travelDate) formData.append('travel_date', travelDate);
        try {
            const { data } = await api.post(`/employer/offers/${candidate.id}/upload-document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            const newDocId = data?.document?.id || null;
            if (docType === 'visa') { setVisaUploaded(true); setVisaFilename(file.name); setVisaDocId(newDocId); }
            else if (docType === 'flight_ticket') { setTicketUploaded(true); setTicketFilename(file.name); setTicketDocId(newDocId); }
            else { setOfferLetterUploaded(true); setOfferLetterFilename(file.name); setOfferLetterDocId(newDocId); }
            const label = docType === 'visa' ? 'Visa' : docType === 'flight_ticket' ? 'Ticket' : 'Offer letter';
            setSuccessMsg(`${label} uploaded successfully`);
            setTimeout(() => setSuccessMsg(''), 3000);
            onUpdate();
        } catch (err) { console.error(err); }
        finally { setUploading(false); }
    };

    // Replace flow: prompt confirmation before overwriting an existing offer document.
    // pendingReplace shape: { file, docType, label } | null
    const [pendingReplace, setPendingReplace] = useState(null);
    // One ref per replace-input so we can programmatically open the file picker
    // from a real onClick handler. The earlier <label>-wraps-<Button> pattern
    // didn't reliably forward clicks to the hidden input (the Button absorbed
    // the click even with pointer-events-none in most browsers).
    const visaReplaceInputRef = useRef(null);
    const ticketReplaceInputRef = useRef(null);
    const offerLetterReplaceInputRef = useRef(null);

    const onPickFile = (file, docType, isReplace) => {
        if (!file) return;
        if (isReplace) {
            const label = docType === 'visa' ? 'visa document' : docType === 'flight_ticket' ? 'flight ticket' : 'offer letter';
            setPendingReplace({ file, docType, label });
        } else {
            uploadDoc(file, docType);
        }
    };

    const confirmReplace = async () => {
        if (!pendingReplace) return;
        const { file, docType } = pendingReplace;
        await uploadDoc(file, docType);
        setPendingReplace(null);
    };

    // Generic preview: fetch worker docs, find by id, open the inline preview modal.
    const previewDoc = async (docId) => {
        if (!docId || !onPreview) return;
        try {
            const { data } = await api.get(`/employer/workers/${candidate.worker_id}`);
            const doc = (data.documents || []).find(d => d.id === docId);
            if (doc) onPreview(doc);
        } catch (err) { console.error(err); }
    };

    const previewOfferLetter = async () => {
        if (!offerLetterDocId || !onPreview) return;
        // Fetch doc metadata so the modal can render correctly
        try {
            const { data } = await api.get(`/employer/workers/${candidate.worker_id}`);
            const doc = (data.documents || []).find(d => d.id === offerLetterDocId);
            if (doc) onPreview(doc);
        } catch (err) { console.error(err); }
    };

    const [savingTravelDate, setSavingTravelDate] = useState(false);
    const saveTravelDate = async () => {
        setSavingTravelDate(true);
        setSuccessMsg('');
        try {
            await api.put(`/employer/offers/${candidate.id}/travel-date`, { travel_date: travelDate || null });
            setSuccessMsg('Travel date saved');
            setTimeout(() => setSuccessMsg(''), 3000);
            onUpdate();
        } catch (err) {
            console.error(err);
        } finally {
            setSavingTravelDate(false);
        }
    };

    return (
        <div className="space-y-4 mt-4" data-testid="visa-manager">
            {successMsg && (
                <div className="bg-green-50 border border-green-200 p-2 text-sm text-green-700 flex items-center gap-2" data-testid="visa-success-msg">
                    <Check className="w-4 h-4" /> {successMsg}
                </div>
            )}

            <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Visa Status</p>
                <div className="flex gap-2">
                    {['submitted', 'processing', 'approved'].map(s => (
                        <Button key={s} size="sm"
                            variant={status === s ? 'default' : 'outline'}
                            className="rounded-none capitalize text-xs flex-1"
                            disabled={updating === s}
                            onClick={() => updateStatus(s)}
                            data-testid={`visa-status-${s}-btn`}
                        >
                            {updating === s ? '...' : s}
                        </Button>
                    ))}
                </div>
                {/* Pre-approval warning: clicking "approved" before any doc is
                    uploaded saves the status but won't notify the worker. */}
                {!visaUploaded && !ticketUploaded && (
                    <p className="text-[11px] text-amber-700 mt-2" data-testid="approve-no-doc-warning">
                        Note: Your worker will not be notified that the visa is approved until you upload at least one document.
                    </p>
                )}
            </div>

            <Separator />

            <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Expected Travel Date (Optional)</p>
                <div className="flex gap-2">
                    <Input type="date" value={travelDate} onChange={e => setTravelDate(e.target.value)} className="h-10 rounded-none flex-1" data-testid="travel-date-input" />
                    <Button
                        type="button"
                        onClick={saveTravelDate}
                        disabled={savingTravelDate}
                        className="h-10 rounded-none bg-zinc-900 text-white text-xs font-semibold"
                        data-testid="save-travel-date-btn"
                    >
                        {savingTravelDate ? '...' : 'Save'}
                    </Button>
                </div>
            </div>

            <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Visa Document</p>
                {!visaUploaded ? (
                    <label className="upload-zone flex items-center justify-center p-4 cursor-pointer">
                        {uploadingVisa ? (<span className="text-sm text-zinc-500">Uploading...</span>) : (
                            <><Upload className="w-5 h-5 text-zinc-400 mr-2" /><span className="text-sm text-zinc-500">Select visa file</span></>
                        )}
                        <input type="file" className="hidden" disabled={uploadingVisa} onChange={e => onPickFile(e.target.files[0], 'visa', false)} data-testid="upload-visa-input" />
                    </label>
                ) : (
                    <div className="flex items-center justify-between p-3 border border-green-200 bg-green-50" data-testid="visa-uploaded-row">
                        <p className="text-xs text-green-700 flex items-center gap-1 min-w-0 truncate">
                            <Check className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">Visa uploaded {visaFilename && `(${visaFilename})`}</span>
                        </p>
                        <div className="flex gap-1 flex-shrink-0">
                            {visaDocId && (
                                <Button size="sm" variant="ghost" className="rounded-none text-xs h-7 text-green-700" onClick={() => previewDoc(visaDocId)} data-testid="preview-visa-btn">
                                    <Eye className="w-3 h-3 mr-1" /> View
                                </Button>
                            )}
                            <Button
                                size="sm" variant="ghost"
                                className="rounded-none text-xs h-7 text-green-700"
                                onClick={() => visaReplaceInputRef.current && visaReplaceInputRef.current.click()}
                                disabled={uploadingVisa}
                                data-testid="replace-visa-btn"
                            >
                                <Upload className="w-3 h-3 mr-1" /> {uploadingVisa ? '...' : 'Replace'}
                            </Button>
                            <input
                                ref={visaReplaceInputRef}
                                type="file"
                                className="hidden"
                                disabled={uploadingVisa}
                                onChange={e => { const f = e.target.files[0]; e.target.value = ''; onPickFile(f, 'visa', true); }}
                                data-testid="replace-visa-input"
                            />
                        </div>
                    </div>
                )}
            </div>

            <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Flight Ticket</p>
                {!ticketUploaded ? (
                    <label className="upload-zone flex items-center justify-center p-4 cursor-pointer">
                        {uploadingTicket ? (<span className="text-sm text-zinc-500">Uploading...</span>) : (
                            <><Upload className="w-5 h-5 text-zinc-400 mr-2" /><span className="text-sm text-zinc-500">Select ticket file</span></>
                        )}
                        <input type="file" className="hidden" disabled={uploadingTicket} onChange={e => onPickFile(e.target.files[0], 'flight_ticket', false)} data-testid="upload-ticket-input" />
                    </label>
                ) : (
                    <div className="flex items-center justify-between p-3 border border-green-200 bg-green-50" data-testid="ticket-uploaded-row">
                        <p className="text-xs text-green-700 flex items-center gap-1 min-w-0 truncate">
                            <Check className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">Ticket uploaded {ticketFilename && `(${ticketFilename})`}</span>
                        </p>
                        <div className="flex gap-1 flex-shrink-0">
                            {ticketDocId && (
                                <Button size="sm" variant="ghost" className="rounded-none text-xs h-7 text-green-700" onClick={() => previewDoc(ticketDocId)} data-testid="preview-ticket-btn">
                                    <Eye className="w-3 h-3 mr-1" /> View
                                </Button>
                            )}
                            <Button
                                size="sm" variant="ghost"
                                className="rounded-none text-xs h-7 text-green-700"
                                onClick={() => ticketReplaceInputRef.current && ticketReplaceInputRef.current.click()}
                                disabled={uploadingTicket}
                                data-testid="replace-ticket-btn"
                            >
                                <Upload className="w-3 h-3 mr-1" /> {uploadingTicket ? '...' : 'Replace'}
                            </Button>
                            <input
                                ref={ticketReplaceInputRef}
                                type="file"
                                className="hidden"
                                disabled={uploadingTicket}
                                onChange={e => { const f = e.target.files[0]; e.target.value = ''; onPickFile(f, 'flight_ticket', true); }}
                                data-testid="replace-ticket-input"
                            />
                        </div>
                    </div>
                )}
            </div>

            <div data-testid="offer-letter-section">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Official Offer Letter (Optional)</p>
                {!offerLetterUploaded ? (
                    <>
                        <label className="upload-zone flex items-center justify-center p-4 cursor-pointer">
                            {uploadingOfferLetter ? (<span className="text-sm text-zinc-500">Uploading...</span>) : (
                                <><Upload className="w-5 h-5 text-zinc-400 mr-2" /><span className="text-sm text-zinc-500">Select offer letter (PDF or image)</span></>
                            )}
                            <input type="file" className="hidden" accept=".pdf,image/*" disabled={uploadingOfferLetter} onChange={e => onPickFile(e.target.files[0], 'offer_letter', false)} data-testid="upload-offer-letter-input" />
                        </label>
                        <p className="text-xs text-zinc-400 mt-1">Optional — upload the worker's official offer letter if available.</p>
                    </>
                ) : (
                    <div className="flex items-center justify-between p-3 border border-green-200 bg-green-50" data-testid="offer-letter-uploaded-row">
                        <p className="text-xs text-green-700 flex items-center gap-1 min-w-0 truncate">
                            <Check className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">Offer letter uploaded {offerLetterFilename && `(${offerLetterFilename})`}</span>
                        </p>
                        <div className="flex gap-1 flex-shrink-0">
                            {offerLetterDocId && (
                                <Button size="sm" variant="ghost" className="rounded-none text-xs h-7 text-green-700" onClick={previewOfferLetter} data-testid="preview-offer-letter-btn">
                                    <Eye className="w-3 h-3 mr-1" /> View
                                </Button>
                            )}
                            <Button
                                size="sm" variant="ghost"
                                className="rounded-none text-xs h-7 text-green-700"
                                onClick={() => offerLetterReplaceInputRef.current && offerLetterReplaceInputRef.current.click()}
                                disabled={uploadingOfferLetter}
                                data-testid="replace-offer-letter-btn"
                            >
                                <Upload className="w-3 h-3 mr-1" /> {uploadingOfferLetter ? '...' : 'Replace'}
                            </Button>
                            <input
                                ref={offerLetterReplaceInputRef}
                                type="file"
                                className="hidden"
                                accept=".pdf,image/*"
                                disabled={uploadingOfferLetter}
                                onChange={e => { const f = e.target.files[0]; e.target.value = ''; onPickFile(f, 'offer_letter', true); }}
                                data-testid="replace-offer-letter-input"
                            />
                        </div>
                    </div>
                )}
            </div>

            {travelDate && (
                <div className="bg-zinc-50 p-3 border border-zinc-200">
                    <p className="text-xs text-zinc-400 uppercase mb-1">Travel Date</p>
                    <p className="text-sm font-semibold">{new Date(travelDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
                </div>
            )}

            <AlertDialog open={!!pendingReplace} onOpenChange={(o) => { if (!o) setPendingReplace(null); }}>
                <AlertDialogContent className="rounded-none border-2 border-zinc-900" data-testid="visa-replace-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading">Replace the current {pendingReplace?.label}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Replace the current {pendingReplace?.label}? The worker will need to re-access their documents.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" data-testid="visa-replace-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-none bg-zinc-900 text-white"
                            onClick={(e) => { e.preventDefault(); confirmReplace(); }}
                            data-testid="visa-replace-confirm-btn"
                        >
                            Confirm
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

/**
 * Compact verification badges shown on talent-pool worker cards.
 * Renders ONLY the badges for documents the worker has actually submitted —
 * never a placeholder, cross, or "missing" indicator. Absent = not submitted.
 */
function VerifBadges({ worker, testIdPrefix }) {
    const items = [
        { show: worker.passport_uploaded, icon: <FileText className="w-3 h-3" />, label: 'Passport', key: 'passport' },
        { show: worker.medical_report_uploaded, icon: <HeartPulse className="w-3 h-3" />, label: 'Medical', key: 'medical' },
        { show: worker.interpol_uploaded, icon: <Shield className="w-3 h-3" />, label: 'Clearance', key: 'clearance' },
        { show: worker.has_video_intro || worker.video_intro_url, icon: <Video className="w-3 h-3" />, label: 'Video', key: 'video' },
        { show: (worker.additional_docs_count || 0) > 0, icon: <Paperclip className="w-3 h-3" />, label: 'Docs +', key: 'docs' },
    ];
    const visible = items.filter(i => i.show);
    if (visible.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`verif-badges-${testIdPrefix}`}>
            {visible.map(i => (
                <span
                    key={i.key}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium border border-zinc-200 bg-zinc-50 text-zinc-700 rounded-sm"
                    data-testid={`verif-${i.key}-${testIdPrefix}`}
                >
                    {i.icon}
                    {i.label}
                </span>
            ))}
        </div>
    );
}


/**
 * MultiSelectFilter — single-source-of-truth filter pill that lets an employer
 * tick multiple values (e.g. Kenya + Uganda + Ghana). Empty array == "all".
 * Built on Radix Popover + Checkbox primitives so behaviour, focus trap, and
 * keyboard nav match the rest of the dashboard. Designed to be a drop-in
 * replacement for the single-Select triggers used previously.
 */
function MultiSelectFilter({ label, options, selected, onChange, allLabel = 'All', triggerClassName = '', testId }) {
    const [open, setOpen] = useState(false);
    const toggle = (opt) => {
        if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
        else onChange([...selected, opt]);
    };
    const summary = selected.length === 0
        ? allLabel
        : selected.length === 1
            ? selected[0]
            : `${selected.length} selected`;
    const isActive = selected.length > 0;
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`${triggerClassName} flex items-center justify-between gap-2 border border-zinc-200 bg-white px-3 text-sm rounded-none hover:border-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 ${isActive ? 'border-zinc-900 font-semibold' : 'text-zinc-700'}`}
                    data-testid={`${testId}-trigger`}
                    aria-label={`${label} filter`}
                >
                    <span className="truncate text-left">{summary}</span>
                    <ChevronDown className="w-4 h-4 opacity-60 flex-shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-56 p-0 rounded-none border-2 border-zinc-900 z-50"
                data-testid={`${testId}-content`}
            >
                <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2">
                    <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-zinc-500">{label}</p>
                    {selected.length > 0 && (
                        <button
                            type="button"
                            onClick={() => onChange([])}
                            className="text-[11px] uppercase tracking-wider text-zinc-500 hover:text-zinc-900"
                            data-testid={`${testId}-clear`}
                        >
                            Clear
                        </button>
                    )}
                </div>
                <div className="max-h-64 overflow-y-auto py-1">
                    {options.map(opt => {
                        const checked = selected.includes(opt);
                        return (
                            <label
                                key={opt}
                                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-zinc-50"
                                data-testid={`${testId}-option-${opt}`}
                            >
                                <Checkbox
                                    checked={checked}
                                    onCheckedChange={() => toggle(opt)}
                                    className="rounded-none"
                                    data-testid={`${testId}-checkbox-${opt}`}
                                />
                                <span className="text-sm">{opt}</span>
                            </label>
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
}


/**
 * EmployerInterviewsTab — dedicated tab listing all interviews the employer
 * has scheduled, split into Upcoming (incl. Live) and Past
 * (Completed/Missed/Cancelled). Read-only past interviews; upcoming ones
 * support Reschedule + Cancel via the parent-owned handlers.
 */
function EmployerInterviewsTab({ interviews, interviewPhotos, cardPhotos, resolveStatus, onReschedule, onCancel, showPast, setShowPast, successMsg }) {
    if (!interviews || interviews.length === 0) {
        return (
            <div className="text-center py-12 border border-zinc-200" data-testid="interviews-tab-empty">
                <Calendar className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                <p className="font-heading font-bold text-base mb-1">No interviews scheduled yet</p>
                <p className="text-sm text-zinc-500 max-w-md mx-auto">Open a worker profile from the Talent Pool and click &quot;Schedule Interview&quot; to set one up.</p>
            </div>
        );
    }
    const withStatus = interviews.map(iv => ({ ...iv, _displayStatus: resolveStatus(iv) }));
    const active = withStatus
        .filter(iv => iv._displayStatus === 'upcoming' || iv._displayStatus === 'live')
        .sort((a, b) => (`${a.date}T${a.time}`).localeCompare(`${b.date}T${b.time}`)); // soonest first
    const past = withStatus
        .filter(iv => ['completed', 'missed', 'cancelled'].includes(iv._displayStatus))
        .sort((a, b) => (`${b.date}T${b.time}`).localeCompare(`${a.date}T${a.time}`)); // most recent first
    const STATUS_BADGE = {
        upcoming: { label: 'Upcoming', cls: 'text-blue-700 border-blue-300 bg-blue-50' },
        live: { label: 'Live Now', cls: 'text-green-700 border-green-400 bg-green-50 animate-pulse' },
        completed: { label: 'Completed', cls: 'text-green-700 border-green-300' },
        missed: { label: 'Missed', cls: 'text-red-600 border-red-300' },
        cancelled: { label: 'Cancelled', cls: 'text-zinc-500 border-zinc-300' },
    };
    const photoFor = (iv) => interviewPhotos[iv.worker_id] || cardPhotos[iv.worker_id] || null;
    return (
        <div data-testid="employer-interviews-tab">
            {successMsg && (
                <div className="mb-4 bg-green-50 border border-green-200 p-2 text-sm text-green-700 flex items-center gap-2" data-testid="interviews-tab-success">
                    <Check className="w-4 h-4" /> {successMsg}
                </div>
            )}
            <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2"><Calendar className="w-5 h-5" /> Upcoming Interviews</h3>
            {active.length === 0 ? (
                <p className="text-sm text-zinc-500 mb-6" data-testid="no-upcoming-interviews">No upcoming interviews.</p>
            ) : (
                <div className="space-y-3 mb-6">
                    {active.map(iv => {
                        const s = iv._displayStatus;
                        const badge = STATUS_BADGE[s];
                        const photo = photoFor(iv);
                        const cardCls = s === 'live' ? 'border-green-300 bg-green-50' : 'border-zinc-200';
                        return (
                            <div key={iv.id} className={`border ${cardCls} p-4`} data-testid={`emp-interview-${iv.id}`}>
                                <div className="flex items-start gap-3">
                                    {photo ? (
                                        <img src={photo} alt={iv.worker_name} className="w-12 h-12 object-cover border border-zinc-200 flex-shrink-0" />
                                    ) : (
                                        <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center flex-shrink-0"><Users className="w-6 h-6 text-zinc-400" /></div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <p className="font-semibold text-sm truncate" data-testid={`emp-interview-worker-${iv.id}`}>{iv.worker_name}</p>
                                            <Badge variant="outline" className={`rounded-none text-xs ${badge.cls}`} data-testid={`emp-interview-status-${iv.id}`}>{badge.label}</Badge>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-zinc-600 mb-2">
                                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {iv.date}</span>
                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {iv.time}</span>
                                        </div>
                                        {iv.meet_link && (
                                            <a href={iv.meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:underline" data-testid={`emp-interview-link-${iv.id}`}>
                                                <ExternalLink className="w-3 h-3" /> {iv.meet_link.length > 60 ? `${iv.meet_link.slice(0, 60)}...` : iv.meet_link}
                                            </a>
                                        )}
                                        <div className="flex gap-2 mt-3">
                                            <Button size="sm" variant="outline" className="rounded-none text-xs h-8 border-zinc-900" onClick={() => onReschedule(iv)} data-testid={`emp-reschedule-${iv.id}`}>
                                                Reschedule
                                            </Button>
                                            <Button size="sm" variant="outline" className="rounded-none text-xs h-8 border-red-500 text-red-600" onClick={() => onCancel(iv)} data-testid={`emp-cancel-${iv.id}`}>
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {past.length > 0 && (
                <div className="border-t border-zinc-100 pt-3" data-testid="emp-past-interviews">
                    <button
                        type="button"
                        onClick={() => setShowPast(v => !v)}
                        className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 hover:text-zinc-900"
                        data-testid="emp-past-interviews-toggle"
                    >
                        {showPast ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        Past Interviews ({past.length})
                    </button>
                    {showPast && (
                        <div className="space-y-2 mt-3">
                            {past.map(iv => {
                                const s = iv._displayStatus;
                                const badge = STATUS_BADGE[s];
                                const photo = photoFor(iv);
                                return (
                                    <div key={iv.id} className="border border-zinc-200 bg-zinc-50 p-3" data-testid={`emp-past-interview-${iv.id}`}>
                                        <div className="flex items-start gap-3">
                                            {photo ? (
                                                <img src={photo} alt={iv.worker_name} className="w-10 h-10 object-cover border border-zinc-200 flex-shrink-0" />
                                            ) : (
                                                <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center flex-shrink-0"><Users className="w-5 h-5 text-zinc-400" /></div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                    <p className="font-semibold text-sm text-zinc-700 truncate">{iv.worker_name}</p>
                                                    <Badge variant="outline" className={`rounded-none text-xs ${badge.cls}`} data-testid={`emp-past-interview-status-${iv.id}`}>{badge.label}</Badge>
                                                </div>
                                                <div className="flex items-center gap-4 text-xs text-zinc-500">
                                                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {iv.date}</span>
                                                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {iv.time}</span>
                                                </div>
                                                {iv.meet_link && s !== 'cancelled' && (
                                                    <a href={iv.meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-700 hover:underline mt-1" data-testid={`emp-past-interview-link-${iv.id}`}>
                                                        <ExternalLink className="w-3 h-3" /> Meet link
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
